import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformDepense } from "@/store/platform/bloc6";
import { toMonthKey } from "@/pages/proprietaire/loyers/periods";
import type { Depense, DepenseInput } from "./types";

interface DepensesContextValue {
  depenses: Depense[];
  addDepense: (input: DepenseInput) => Depense;
  getDepense: (id: string) => Depense | undefined;
  removeDepense: (id: string) => void;
  updateDepense: (id: string, input: DepenseInput) => void;
  depensesByImmeuble: (immeubleId: string) => Depense[];
  depensesByBien: (bienId: string) => Depense[];
  countByImmeuble: (immeubleId: string) => number;
  totalByImmeuble: (immeubleId: string) => number;
  stats: { total: number; montantTotal: number; ceMois: number };
}

const DepensesContext = createContext<DepensesContextValue | null>(null);

function toOwnerDepense(source: PlatformDepense): Depense {
  return {
    id: source.id,
    libelle: source.libelle,
    categorie: source.categorie,
    montant: source.montant,
    date: source.date,
    immeubleId: source.immeubleId,
    bienId: source.bienId,
    description: source.description,
    justificatif: source.justificatif,
    createdAt: source.createdAt,
  };
}

/**
 * Vue « Propriétaire » des dépenses du store unifié de la plateforme.
 * Les dépenses proviennent RÉELLEMENT de la table `depenses` (Supabase) : la
 * portée est déduite de l'immeuble ou du bien ciblé. Chaque opération (ajout,
 * modification, suppression) passe par Supabase avec mise à jour optimiste et
 * rollback en cas d'erreur. Aucune donnée fictive, aucune persistance locale.
 */
export function DepensesProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const depenses = useMemo(
    () => platform.depenses.filter((item) => item.scope === "proprietaire").map(toOwnerDepense),
    [platform.depenses]
  );

  const addDepense = useCallback(
    (input: DepenseInput) => toOwnerDepense(platform.addDepense(input)),
    [platform.addDepense]
  );

  const removeDepense = useCallback(
    (id: string) => {
      platform.removeDepense(id);
    },
    [platform.removeDepense]
  );

  const updateDepense = useCallback(
    (id: string, input: DepenseInput) => {
      platform.updateDepense(id, input);
    },
    [platform.updateDepense]
  );

  const getDepense = useCallback((id: string) => depenses.find((item) => item.id === id), [depenses]);

  const depensesByImmeuble = useCallback(
    (immeubleId: string) => depenses.filter((item) => item.immeubleId === immeubleId),
    [depenses]
  );

  const depensesByBien = useCallback(
    (bienId: string) => depenses.filter((item) => item.bienId === bienId),
    [depenses]
  );

  const countByImmeuble = useCallback(
    (immeubleId: string) => depenses.filter((item) => item.immeubleId === immeubleId).length,
    [depenses]
  );

  const totalByImmeuble = useCallback(
    (immeubleId: string) =>
      depenses.filter((item) => item.immeubleId === immeubleId).reduce((sum, item) => sum + item.montant, 0),
    [depenses]
  );

  const stats = useMemo(() => {
    const moisCourant = toMonthKey(new Date());
    return {
      total: depenses.length,
      montantTotal: depenses.reduce((sum, item) => sum + item.montant, 0),
      ceMois: depenses
        .filter((item) => toMonthKey(new Date(item.date)) === moisCourant)
        .reduce((sum, item) => sum + item.montant, 0),
    };
  }, [depenses]);

  const value = useMemo(
    () => ({
      depenses,
      addDepense,
      getDepense,
      removeDepense,
      updateDepense,
      depensesByImmeuble,
      depensesByBien,
      countByImmeuble,
      totalByImmeuble,
      stats,
    }),
    [
      depenses,
      addDepense,
      getDepense,
      removeDepense,
      updateDepense,
      depensesByImmeuble,
      depensesByBien,
      countByImmeuble,
      totalByImmeuble,
      stats,
    ]
  );

  return <DepensesContext.Provider value={value}>{children}</DepensesContext.Provider>;
}

export function useDepenses(): DepensesContextValue {
  const context = useContext(DepensesContext);
  if (!context) {
    throw new Error("useDepenses doit être utilisé à l'intérieur de DepensesProvider");
  }
  return context;
}